import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../services/api';
import type { User, PaginationInfo } from '../types';
import { Layout } from '../components/layout/Layout';
import { UserSearch } from '../components/users/UserSearch';
import { UserTable } from '../components/users/UserTable';
import { DeleteUserModal } from '../components/users/DeleteUserModal';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Card } from '../components/ui/Card';
import { useToast } from '../components/ui/Toast';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export function Users() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [userType, setUserType] = useState('');
  const [joinedFrom, setJoinedFrom] = useState('');
  const [joinedTo, setJoinedTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getUsers({
        search: searchTerm,
        userType: userType || undefined,
        joinedFrom: joinedFrom || undefined,
        joinedTo: joinedTo || undefined,
        page: currentPage,
        limit: 20,
        sortBy: sortDir === 'desc' ? `-${sortBy}` : sortBy,
      });
      setUsers(response.users);
      setPagination(response.pagination);
    } catch (err) {
      console.error('Failed to load users:', err);
      toast(err instanceof Error ? err.message : 'Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, toast, userType, joinedFrom, joinedTo, currentPage, sortBy, sortDir]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setUserType('');
    setJoinedFrom('');
    setJoinedTo('');
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const handleViewUser = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  const handleToggleStatus = (user: User) => {
    setSelectedUser(user);
    setShowDisableModal(true);
  };

  const confirmToggleStatus = async () => {
    if (!selectedUser) return;

    try {
      setUpdating(true);
      await api.updateUserStatus(selectedUser.id, !selectedUser.disabled);
      setShowDisableModal(false);
      toast(
        `${selectedUser.name} has been ${selectedUser.disabled ? 'enabled' : 'disabled'} successfully`,
        'success'
      );
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to update user status.', 'error');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[#f8fafc]">Users</h2>
            <p className="text-[#94a3b8] mt-1">
              Manage and view all platform users
            </p>
          </div>
        </div>

        {/* Search and Filters */}
        <Card>
          <UserSearch
            searchTerm={searchTerm}
            onSearchChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            userType={userType}
            onUserTypeChange={(value) => {
              setUserType(value);
              setCurrentPage(1);
            }}
            joinedFrom={joinedFrom}
            onJoinedFromChange={(value) => {
              setJoinedFrom(value);
              setCurrentPage(1);
            }}
            joinedTo={joinedTo}
            onJoinedToChange={(value) => {
              setJoinedTo(value);
              setCurrentPage(1);
            }}
            onClearFilters={handleClearFilters}
          />
        </Card>

        {/* Users Table */}
        <Card padding="none">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-16 text-[#94a3b8]">
              No users found
            </div>
          ) : (
            <>
              <UserTable
                users={users}
                onViewUser={handleViewUser}
                onToggleStatus={handleToggleStatus}
                onDeleteUser={setUserToDelete}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-[#2a2a3a]">
                  <p className="text-sm text-[#94a3b8]">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} users
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={pagination.page === 1}
                      icon={<ChevronLeft className="w-4 h-4" />}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-[#94a3b8] px-2">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                      disabled={pagination.page === pagination.totalPages}
                      icon={<ChevronRight className="w-4 h-4" />}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <DeleteUserModal
        user={userToDelete}
        onClose={() => setUserToDelete(null)}
        onDeleted={async () => {
          setUserToDelete(null);
          await fetchUsers();
        }}
      />

      {/* Disable/Enable Modal */}
      <Modal
        isOpen={showDisableModal}
        onClose={() => {
          setShowDisableModal(false);
          setSelectedUser(null);
        }}
        title={selectedUser?.disabled ? 'Enable User' : 'Disable User'}
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setShowDisableModal(false);
                setSelectedUser(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant={selectedUser?.disabled ? 'primary' : 'danger'}
              onClick={confirmToggleStatus}
              loading={updating}
            >
              {selectedUser?.disabled ? 'Enable User' : 'Disable User'}
            </Button>
          </>
        }
      >
        <p className="text-[#94a3b8]">
          Are you sure you want to {selectedUser?.disabled ? 'enable' : 'disable'}{' '}
          <span className="text-[#f8fafc] font-medium">{selectedUser?.name}</span>?
        </p>
        <p className="text-[#64748b] text-sm mt-2">
          {selectedUser?.disabled
            ? 'This will restore the user\'s access to the platform.'
            : 'This will prevent the user from accessing the platform.'}
        </p>
      </Modal>
    </Layout>
  );
}
